const DASH = "—";

/** A count, or a dash when the source has none (null) — a real 0 stays 0. */
export function fmtCount(value: number | null | undefined): string {
  return value == null ? DASH : String(value);
}

/**
 * A 0..1 fraction as a percentage ("0.476" becomes "47.6%"). Standings use fractions.
 * The `typeof` check, here and below, is for a payload that missed its view schema and
 * arrived as the raw data: a string or an object must show a dash, not throw.
 */
export function fmtFraction(value: number | null | undefined): string {
  return typeof value === "number"
    ? `${Number((value * 100).toFixed(1))}%`
    : DASH;
}

/** At most two decimals, no trailing zeros: 2.57, 56, 0.4. */
export function fmtNumber(value: number | null | undefined): string {
  return typeof value === "number" ? String(Number(value.toFixed(2))) : DASH;
}

const PERCENT_KEY = /possession|accuracy|percent|pct/i;

const isUnit = (n: unknown): n is number =>
  typeof n === "number" && n >= 0 && n <= 1;

/**
 * One match statistic as the source gave it. The source mixes units: possession and shots
 * accuracy are 0..1 fractions (0.4), other percentages are 0..100 (58.54), and nothing in the
 * payload says which a key is. A key or label that names a percentage is shown with a % sign,
 * and the pair is read as fractions only when BOTH sides are within 0..1, so a 0..100 pair
 * such as 1 and 99 stays 1% and 99%. Pass the other side as `other`. The one case this cannot
 * tell apart is a 0..100 pair that is entirely at or below 1 (say 0 and 1), read as fractions.
 */
export function fmtStat(
  stat: { key?: string | null; label?: string | null },
  value: number | null | undefined,
  other?: number | null,
): string {
  if (typeof value !== "number") return DASH;
  const isPercent = PERCENT_KEY.test(`${stat.key ?? ""} ${stat.label ?? ""}`);
  if (!isPercent) return fmtNumber(value);
  const isFraction = isUnit(value) && (other == null || isUnit(other));
  return `${Number((isFraction ? value * 100 : value).toFixed(1))}%`;
}

/** The share of a home-vs-away pair that is the home side's, for a comparison bar. */
export function homeShare(
  home: number | null | undefined,
  away: number | null | undefined,
): number | undefined {
  if (typeof home !== "number" || typeof away !== "number") return undefined;
  if (home < 0 || away < 0) return undefined;
  const total = home + away;
  return total > 0 ? (home / total) * 100 : undefined;
}

/**
 * Only an absolute http(s) URL becomes a link. The backend validates clip URLs, but IMS
 * never trusts a stored string as an href: a `javascript:` value must render as text.
 */
export function safeHttpUrl(
  url: string | null | undefined,
): string | undefined {
  return url && /^https?:\/\//i.test(url) ? url : undefined;
}
